import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import { Formik, Form } from 'formik'
import classNames from 'classnames'

/* Hooks */
import { useToggleState } from '../react-helpers'
import { ConnectedWallet } from '@privy-io/react-auth'

/* Helpers */
import { resolveENS, getENSFromAddress } from '../api'
import { isValidAddress, isValidEmail } from '../lib/helpers'
import { useLedgerContext } from 'ScanPage/LedgerContext'
import { Loading } from '../components/Loading'
import { usePrivyWrapper } from '../hooks/usePrivyWrapper'

type ChooseAddressPageProps = {
  onAccountDetails: (addressOrENS: string, address: string) => void
}

type LoginButtonProps = {
  onAddress: (addressOrENS: string, address: string) => void
}

type AddressInputProps = {
  onAddress: (addressOrENS: string, address: string) => void
}

type AddressFormValues = {
  address: string
}

const initialValues: AddressFormValues = {
  address: '',
}

const LoginButton: React.FC<LoginButtonProps> = ({ onAddress }) => {
  const { connectWallet, recentConnectedWallet } = usePrivyWrapper()
  const [isLoading, setIsLoading] = useState(false)

  const handleWalletUpdate = useCallback(
    (wallet: ConnectedWallet) => {
      setIsLoading(true)
      const address = wallet.address

      getENSFromAddress(address)
        .then((ensResponse) => {
          onAddress(ensResponse.valid ? ensResponse.ens : address, address)
        })
        .catch((e) => {
          onAddress(address, address)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [onAddress],
  )

  useEffect(() => {
    if (!recentConnectedWallet || isLoading) return

    handleWalletUpdate(recentConnectedWallet)
  }, [handleWalletUpdate, recentConnectedWallet, isLoading])

  return (
    <button className="btn" onClick={connectWallet} disabled={isLoading}>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <span>Explore my collection</span>
          <span className="second-text">Connect wallet</span>
        </>
      )}
    </button>
  )
}

const AddressInput: React.FC<AddressInputProps> = ({ onAddress }) => {
  const [ensError, setEnsError] = useState(false)
  const [addressError, setAddressError] = useState(false)
  const [working, setWorking] = useState(false)

  const onSubmit = async ({ address }: AddressFormValues) => {
    setWorking(true)
    setEnsError(false)
    setAddressError(false)
    if (isValidEmail(address)) {
      onAddress(address, address)
    } else if (address.includes('.')) {
      const ensResponse = await resolveENS(address)

      if (ensResponse.valid) {
        onAddress(address, ensResponse.ens)
      } else {
        setEnsError(true)
      }
    } else if (isValidAddress(address)) {
      try {
        const addressResponse = await getENSFromAddress(address)
        onAddress(addressResponse.valid ? addressResponse.ens : address, address)
      } catch (e) {
        onAddress(address, address)
      }
    } else {
      setAddressError(true)
    }

    setWorking(false)
  }

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues}>
      {({ values, errors, setFieldValue }) => (
        <Form className="login-form">
          <input
            type="text"
            id="address"
            name="address"
            placeholder="matoken.eth or alison@google.com"
            onChange={(e) => setFieldValue('address', e.target.value.trim(), true)}
            autoComplete="off"
            value={values.address}
            className={classNames(ensError && 'error')}
          />
          {ensError && <p className="text-error">Invalid ENS name</p>}
          {addressError && <p className="text-error">Invalid Address</p>}
          <input
            type="submit"
            id="submit"
            value={working ? '' : 'Explore my collection'}
            disabled={!values.address}
            className={classNames(working && 'loading')}
            name="submit"
          />
        </Form>
      )}
    </Formik>
  )
}

export const ChooseAddressPage: React.FC<ChooseAddressPageProps> = ({ onAccountDetails }) => {
  const [enterByHand, toggleEnterByHand] = useToggleState(false)
  const { ledger: { address } = {} } = useLedgerContext()

  useEffect(() => {
    if (address && onAccountDetails) {
      onAccountDetails(address, address)
    }
  }, [address, onAccountDetails])

  return (
    <main id="site-main" role="main" className="app-content">
      <div className="container">
        <div className="content-event" data-aos="fade-up" data-aos-delay="300">
          <p>Welcome to POAP. Use this website for browsing your collection of POAPs.</p>
          <br />
          {enterByHand ? (
            <AddressInput onAddress={onAccountDetails} />
          ) : (
            <>
              <LoginButton onAddress={onAccountDetails} />
              <p>
                or{' '}
                <a
                  href="/"
                  onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                    event.preventDefault()
                    toggleEnterByHand()
                  }}
                >
                  input an address manually
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
